import AssetGroup from '@play-co/timestep-core/lib/ui/resource/AssetGroup';
import { getMatch2SoundEffectsFiles } from './game/minigame/match2-timestep/game/defs/audio';
import { getGoalsPath } from './game/minigame/match2-timestep/game/util/assets';
import {
  getRegionPath,
  loadRegionAssets,
} from './game/minigame/match2-timestep/match2.assets';
import { loader } from '@play-co/timestep-core';
import { user } from './StateObserver';
import { getRegionId } from './replicant/match2/regions/regions.getters';
import { Region } from './replicant/match2/regions/regions.ruleset';
import { getMatch2Level } from './replicant/match2/match2.getters';
import { getBlockAssetPath } from './loadingGroups';
import { matchTheme } from './game/theme/match2/Match2Theme';
import { isDynamicTestInBucket } from './replicant/getters/ab';
import ab from './replicant/ruleset/ab';
import * as Tutorial from './replicant/tutorial';
import { isLoadingFTUEActive } from './lib/ftue';
import { PuzzleMapImporter } from './game/minigame/match2-timestep/game/util/PuzzleMapImporter';
import { BlockId } from './game/minigame/match2-timestep/game/defs/block';

/**
 * Loads assets required for a Match2 game level.
 *
 * @remarks
 * This function prepares all necessary assets for a specific Match2 level, including region-specific resources,
 * block assets, particles, platform graphics, and sound effects. The background
 * music is not part of it: `AudioManager` loads it on demand, and the
 * `match2Music` optimistic resource gets its download going early.
 *
 * @param level - Optional level number. If not provided, the current user's level will be retrieved.
 * @returns A promise that resolves when all assets are loaded
 *
 * @throws {Error} Potential errors during asset loading or JSON parsing
 */
export async function loadMatch2LevelAssets(level?: number) {
  // Load region assets. Not awaited yet: the region JSON says which blocks a
  // level uses, which the FTUE path below does not need.
  const regionAssets = loadRegionAssets();

  // Get region data.
  if (!level) level = getMatch2Level(user(), true);

  // If FTUE loading is active, load only the minimal assets for the 1st level.
  // Its asset list is fixed, so it is requested alongside the region JSON
  // rather than a round trip behind it. The region is still awaited here
  // because the puzzle map is read from it as soon as the scene opens.
  if (isLoadingFTUEActive() && !process.env.IS_EDITOR) {
    const [assets] = await Promise.all([
      getFirstPuzzleRequiredAssets().load(),
      regionAssets,
    ]);
    return assets;
  }

  await regionAssets;

  const regionPath = getRegionPath(getRegionId(user(), level));
  const { blocks } = (await loader.loadJSON(regionPath)) as unknown as Region;
  const { blockAssetPaths, blockAssetDescriptions } = process.env.IS_EDITOR
    ? getBlockAssetPath()
    : getBlockAssetPath(blocks);

  return new AssetGroup(
    [
      ...AssetGroup.constructURLs([
        'assets/particles/',
        getGoalsPath(),
        `assets/minigame/match2/frame.tooltip.png`,
        ...blockAssetPaths,
        ...matchTheme().getAssets(),
      ]),
      ...blockAssetDescriptions,
      // Load all match2 sound effects
      ...getMatch2SoundEffectsFiles(),
      ...getTutorialExtraAssets(),
    ],
    undefined,
    AssetGroup.PRIORITY_MEDIUM,
  ).load();
}

/**
 * The essential assets of the first puzzle level, loaded instead of the
 * level's full asset set while the FTUE is active.
 */
export function getFirstPuzzleRequiredAssets() {
  return new AssetGroup(
    getFirstPuzzleAssetDescriptions(),
    undefined,
    AssetGroup.PRIORITY_MEDIUM,
  );
}

/**
 * Everything the FTUE branch of `loadMatch2LevelAssets` requests, in one
 * group: the first puzzle's assets and the region JSON its map is read from.
 * For warming the loader's cache before `startMatch2` asks; the loader dedupes
 * requests, so the real load then joins these or hits the cache.
 */
export function getFirstPuzzleWarmUpAssets() {
  return new AssetGroup(
    [
      ...AssetGroup.constructURLs(getRegionPath(getRegionId(user()))),
      ...getFirstPuzzleAssetDescriptions(),
    ],
    undefined,
    AssetGroup.PRIORITY_MEDIUM,
  );
}

function getFirstPuzzleAssetDescriptions() {
  const { blockAssetPaths, blockAssetDescriptions } = getBlockAssetPath([
    'basicBlue',
    'basicGreen',
    'basicPurple',
    'basicRed',
    'basicYellow',
    'hatAll',
  ]);

  return [
    ...AssetGroup.constructURLs([
      getGoalsPath(),
      ...blockAssetPaths,
      ...matchTheme().getAssets(),
    ]),
    ...blockAssetDescriptions,
    ...getTutorialExtraAssets(),
  ];
}

/**
 * Retrieves additional tutorial assets based on the user's tutorial state and A/B test bucket.
 *
 * @remarks
 * This function determines whether to load extra tutorial assets by checking:
 * 1. If the 'puzzleCoinsBasic' tutorial step is incomplete
 * 2. If the user is in a dynamic test bucket for tutorial revamp
 *
 * @returns An array of asset paths for the tutorial, or an empty array if no extra assets are needed
 */
export function getTutorialExtraAssets() {
  const state = user();

  // Do not load extra assets if the tutorial is complete
  if (Tutorial.getters.isTutorialStepComplete(state, 'puzzleCoinsBasic')) {
    return [];
  }

  // Alternative tutorial sequence for the tutorial revamp test
  return [
    'assets/tutorial/revamp/tutorial_tip.png',
    'assets/tutorial/revamp/tutorial_arrow.png',
    'assets/tutorial/revamp/tutorial_tip_big.png',
  ];
}

/**
 * Gets the block asset paths and descriptions used in a puzzle map.
 * @param puzzleRawData - The raw string data of the puzzle map.
 * @returns An object containing block asset paths and descriptions.
 */
export function getPuzzleAssets(puzzleRawData: string) {
  // Parse the puzzle map data
  const mapDef = new PuzzleMapImporter(puzzleRawData).import();
  if (!mapDef) throw new Error('Invalid map def');

  // List of blocks used in the map
  const blocks: BlockId[] = [];

  // Include goals.
  Object.keys(mapDef.goals).forEach((key) => {
    if (!blocks.includes(key as BlockId)) {
      blocks.push(key as BlockId);
    }
  });

  // Include spawns.
  mapDef.spawns.forEach((id) => {
    if (!blocks.includes(id)) {
      blocks.push(id);
    }
  });

  // Include blocks.
  mapDef.grid.cells.flat().forEach((cell) => {
    cell?.blocks?.forEach(({ id }) => {
      if (!blocks.includes(id)) {
        blocks.push(id);
      }
    });
  });

  // Get asset paths and descriptions for the identified blocks
  return getBlockAssetPath(blocks);
}
